import React, { FunctionComponent, ReactNode } from 'react'
import { NumericFormat } from 'react-number-format';
import { Paper, Stack, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery } from '@mui/material'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import RemoveIcon from '@mui/icons-material/Remove';
import { TooltipButton } from '../../miscellaneous-components/tooltip-button';
import gaiaTheme from '../../../../root-components/theme';

const ComparisonRow: React.FC<{ diff: number; units?: string; label?: string }> = ({ diff, units, label }) => {
  const isDecrease = diff < 0;
  const isNoChange = diff === 0;
  const iconColor = isNoChange ? 'text.secondary' : isDecrease ? 'success.main' : 'error.main';
  const Icon = isNoChange ? RemoveIcon : isDecrease ? ArrowDownwardIcon : ArrowUpwardIcon;
  return (
    <Stack direction='row' alignItems='center' spacing={0.25} sx={{ mt: 0.5 }}>
      <Icon sx={{ fontSize: '0.85rem', color: iconColor }} />
      <Typography variant='caption' sx={{ color: 'text.secondary' }}>
        <NumericFormat value={Math.abs(diff)} displayType='text' thousandSeparator=',' decimalScale={2} fixedDecimalScale />
        {units ? ` ${units}` : ''}{label ? ` ${label}` : ''}
      </Typography>
    </Stack>
  );
};

export const AnalyticMetric: FunctionComponent<{
  title: string,
  titleTwo?: string,
  tooltip?: string,
  value: number,
  valueTwo?: number;
  units?: string,
  negative?: boolean,
  toggleOne?: string,
  toggleTwo?: string,
  compact?: boolean,
  comparison?: number,
  comparisonTwo?: number,
  comparisonLabel?: string,
  unavailable?: boolean,
  unavailableMessage?: string,
  /** Let the tile grow beyond its grid row instead of clipping/overflowing when its content
   * wraps taller than its siblings (used by the readiness score band). Default keeps the
   * dashboard's original equal-height behaviour. */
  fitContent?: boolean,
  /** Extra content under the value row (e.g. provenance chips); rendered in place of nothing, never instead of the comparison. */
  footer?: ReactNode,
  onClick?: () => void,
}> = ({
  title,
  titleTwo,
  tooltip,
  value,
  valueTwo,
  toggleOne,
  toggleTwo,
  units,
  compact = false,
  negative = false,
  comparison,
  comparisonTwo,
  comparisonLabel,
  unavailable = false,
  unavailableMessage,
  fitContent = false,
  footer,
  onClick }) => {

    const [status, setStatus] = React.useState<string>('toggleOne');
    const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));

    const statusHandler = (e: React.MouseEvent<HTMLElement>, newStatus: string | null) => {
      if (newStatus !== null) {
        setStatus(newStatus)
      }
    }

    return (
      <Paper
        variant='outlined'
        onClick={onClick}
        sx={{
          p: compact ? 2 : 4,
          // Default: the dashboard's original equal-height behaviour (percentage height,
          // content-box Paper since the app loads no CssBaseline). fitContent swaps to
          // border-box + minHeight so a tile whose content wraps taller than its grid row
          // grows the row instead of overflowing onto whatever sits below.
          ...(fitContent
            ? { boxSizing: 'border-box', minHeight: '100%' }
            : { height: '100%' }),
          ...(onClick && {
            cursor: 'pointer',
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
            '&:hover': {
              borderColor: 'primary.main',
              boxShadow: 1,
            },
          }),
        }}
      >
        {titleTwo &&
          <Stack sx={{ float: 'right' }}>
            <ToggleButtonGroup value={status} exclusive onChange={statusHandler}>
              <ToggleButton value='toggleOne' sx={{ p: 0, width: '65px' }}><Typography variant='body1' sx={{ color: 'text.primary', textTransform: 'capitalize', fontSize: '0.85rem', }}>{toggleOne}</Typography></ToggleButton>
              <ToggleButton value='toggleTwo' sx={{ p: 0, width: '65px' }}><Typography variant='body1' sx={{ color: 'text.primary', textTransform: 'capitalize', fontSize: '0.85rem', }}>{toggleTwo}</Typography></ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        }
        {status === 'toggleOne' &&
          <>
            <Stack direction='row' alignItems={'center'}>
              <Typography variant={compact ? 'body2' : 'subtitle1'} sx={{ flex: 1 }}>
                <b>{title}</b>
              </Typography>
              {tooltip && !isMobileMode && <span onClick={(e) => e.stopPropagation()}><TooltipButton
                text={tooltip}
                noPadding={true}
                placement='right' /></span>}
            </Stack>
            {unavailable ? (
              <Typography variant={compact ? 'h5' : 'h4'} color='text.secondary'>N/A</Typography>
            ) : (
              <Stack direction='row' spacing={1} sx={{
                alignItems: 'baseline',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                <Typography variant={compact ? 'h5' : 'h4'} color='text.primary'>
                  <NumericFormat value={negative ? - value : value} displayType={'text'} thousandSeparator="," decimalScale={2} />
                </Typography>
                {units && <Typography variant={compact ? 'body2' : 'body1'} color='text.primary'>
                  {units}
                </Typography>}
              </Stack>
            )}
            {unavailable && unavailableMessage &&
              <Typography variant='caption' sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>{unavailableMessage}</Typography>}
            {!unavailable && comparison !== undefined && <ComparisonRow diff={comparison} units={units} label={comparisonLabel} />}
            {!unavailable && footer}
          </>
        }
        {status === 'toggleTwo' && titleTwo &&
          <>
            <Stack direction='row' alignItems={'center'}>
              <Typography variant={compact ? 'body2' : 'subtitle1'} sx={{ flex: 1 }}>
                <b>{titleTwo}</b>
              </Typography>
              {tooltip && !isMobileMode && <span onClick={(e) => e.stopPropagation()}><TooltipButton
                text={tooltip}
                noPadding={true}
                placement='right' /></span>}
            </Stack>
            <Stack direction='row' spacing={1} sx={{
              alignItems: 'baseline', whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              <Typography variant={compact ? 'h5' : 'h4'} color='text.primary'>
                {valueTwo && <NumericFormat value={negative ? - valueTwo : valueTwo} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale={true} />}
              </Typography>
              {units && <Typography variant={compact ? 'body2' : 'body1'} color='text.primary'>
                {units}
              </Typography>}
            </Stack>
            {comparisonTwo !== undefined && <ComparisonRow diff={comparisonTwo} units={units} label={comparisonLabel} />}
          </>}
      </Paper>
    )
  }